import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';

import { Colors, Variants } from '@/components/ui/types';
import useStudentReportRowPermissions from '@/data/hook/scheduled-lesson-report/useStudentReportRowPermissions';
import Klass from '@/models/Klass';
import ScheduledLesson from '@/models/ScheduledLesson';

type UseSubmitBtnStylesProps = {
  scheduledLesson: ScheduledLesson;
  klass: Klass;
  hasDone: boolean;
};

type ButtonState = 'finish' | 'unfinish' | 'block' | 'missing';

type ButtonContent = {
  color: Colors;
  variant: Variants;
  text: string;
  icon: JSX.Element;
  tooltipMessage?: string;
};

const LOCKED_STATES: ButtonState[] = ['block', 'missing'];

export default function useSubmitBtnStyles({
  scheduledLesson,
  klass,
  hasDone,
}: UseSubmitBtnStylesProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'scheduledLessonReportModal',
  });

  const { t: tReport } = useTranslation('translation', {
    keyPrefix: 'manageScheduledLessonReport',
  });

  const { permitions } = useStudentReportRowPermissions({
    scheduledLesson,
    klass,
  });

  const checkIcon = <CheckIcon className="size-4" />;

  const blockContent: ButtonContent = {
    color: hasDone ? 'success' : 'neutral',
    variant: 'soft',
    text: hasDone
      ? tReport('classCompletedButtonPhrase')
      : t('submitLessonReport'),
    icon: checkIcon,
    tooltipMessage: hasDone
      ? tReport('hasDoneDesmark')
      : tReport('hasNoPermission'),
  };

  const buttonContents: Record<ButtonState, ButtonContent> = {
    finish: {
      color: 'primary',
      variant: 'default',
      text: t('submitLessonReport'),
      icon: checkIcon,
    },

    unfinish: {
      color: 'error',
      variant: 'default',
      text: tReport('classNotCompleted'),
      icon: <XMarkIcon className="size-4" />,
    },

    block: blockContent,

    missing: {
      ...blockContent,
      color: 'neutral',
      text: t('submitLessonReport'),
      tooltipMessage: tReport('missingRequiredFields'),
    },
  };

  function getButtonState(): ButtonState {
    if (!permitions.hasPresencePermission) return 'block';
    if (hasDone) return 'unfinish';
    if (!permitions.canFinishReport) return 'missing';
    return 'finish';
  }

  const buttonState = getButtonState();

  return {
    ...buttonContents[buttonState],
    isLocked: LOCKED_STATES.includes(buttonState),
  };
}
